/**
 * Alta y edición de un **sub-activo** de mantenimiento.
 *
 * Puerto del formulario modal de `views/maintenance/sub_assets/index.php`
 * (activo, código, nombre, tipo y estado) más los campos del umbral, que el panel
 * anterior no dejaba tocar: sin ellos el umbral se quedaba siempre en el valor
 * por defecto de la tabla y el aviso de mantenimiento nunca reflejaba la realidad.
 */

import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { cn, formatNumber } from '@/lib/utils';

import type { ActivoDeSubActivo, SubActivo, SubActivoPayload } from '../sub-activos.types';

/** Estilo de los controles nativos (mismo que el resto del panel). */
const CONTROL =
  'h-12 w-full rounded-control border border-ink-200 bg-white px-4 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const MAX_CODIGO = 50;
const MAX_NOMBRE = 150;
const MAX_TIPO = 50;
const UMBRAL_POR_DEFECTO = 300000;

/** Etiqueta de un campo que no usa el componente `Input`. */
function Etiqueta({ htmlFor, children }: { htmlFor: string; children: string }) {
  return (
    <label htmlFor={htmlFor} className="text-sm font-medium text-ink-700">
      {children}
    </label>
  );
}

/** Número desde el formulario (vacío = 0). */
function aNumero(valor: string): number {
  const convertido = Number(valor);

  return Number.isFinite(convertido) && convertido >= 0 ? convertido : 0;
}

export function SubActivoDialogo({
  subActivo,
  activos,
  guardando,
  error,
  onGuardar,
  onCerrar,
}: {
  subActivo: SubActivo | null;
  activos: ActivoDeSubActivo[];
  guardando: boolean;
  error: string | null;
  onGuardar: (payload: SubActivoPayload) => void;
  onCerrar: () => void;
}) {
  const [activoId, setActivoId] = useState(subActivo?.activo_id ?? activos[0]?.id ?? 0);
  const [codigo, setCodigo] = useState(subActivo?.codigo ?? '');
  const [nombre, setNombre] = useState(subActivo?.nombre ?? '');
  const [tipo, setTipo] = useState(subActivo?.tipo_componente ?? '');
  const [descripcion, setDescripcion] = useState(subActivo?.descripcion ?? '');
  const [estado, setEstado] = useState<'activo' | 'desuso'>(
    subActivo ? (subActivo.activo_estado ? 'activo' : 'desuso') : 'activo',
  );
  const [umbral, setUmbral] = useState(String(subActivo?.umbral ?? UMBRAL_POR_DEFECTO));
  const [lecturaActual, setLecturaActual] = useState(String(subActivo?.lectura_actual ?? 0));
  const [lecturaCambio, setLecturaCambio] = useState(String(subActivo?.lectura_cambio ?? 0));
  const [ultimo, setUltimo] = useState(subActivo?.ultimo_mantenimiento ?? '');
  const [proximo, setProximo] = useState(subActivo?.proximo_mantenimiento ?? '');
  const [critico, setCritico] = useState(subActivo?.critico ?? false);

  const codigoLimpio = codigo.trim();
  const nombreLimpio = nombre.trim();

  const errorCodigo =
    codigoLimpio.length === 0
      ? 'El código es obligatorio.'
      : codigoLimpio.length > MAX_CODIGO
        ? `Máximo ${MAX_CODIGO} caracteres.`
        : null;
  const errorNombre =
    nombreLimpio.length === 0
      ? 'El nombre es obligatorio.'
      : nombreLimpio.length > MAX_NOMBRE
        ? `Máximo ${MAX_NOMBRE} caracteres.`
        : null;

  const usado = Math.max(0, aNumero(lecturaActual) - aNumero(lecturaCambio));
  const limite = aNumero(umbral);
  const progreso = limite > 0 ? Math.min(100, Math.round((usado / limite) * 100)) : 0;
  const valido = activoId > 0 && !errorCodigo && !errorNombre;

  return (
    <Dialog
      open
      onClose={onCerrar}
      size="lg"
      title={subActivo ? 'Editar sub-activo' : 'Nuevo sub-activo'}
      description={
        subActivo
          ? 'El consumo se recalcula con las lecturas: desde el último cambio hasta la lectura actual.'
          : 'Registra la pieza que se mantiene y el umbral de galonaje que dispara el aviso.'
      }
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={!valido}
            onClick={() =>
              onGuardar({
                activo_id: activoId,
                codigo: codigoLimpio,
                nombre: nombreLimpio,
                tipo_componente: tipo.trim(),
                descripcion: descripcion.trim(),
                umbral: limite,
                lectura_actual: aNumero(lecturaActual),
                lectura_cambio: aNumero(lecturaCambio),
                ultimo_mantenimiento: ultimo,
                proximo_mantenimiento: proximo,
                critico,
                activo: estado === 'activo',
              })
            }
          >
            {subActivo ? 'Guardar cambios' : 'Crear sub-activo'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <Alert variant="error" title="No se pudo guardar">
            {error}
          </Alert>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Etiqueta htmlFor="sub-activo-padre">Activo</Etiqueta>
            <Select
              id="sub-activo-padre"
              className={CONTROL}
              value={String(activoId)}
              onChange={(evento) => setActivoId(Number(evento.target.value))}
            >
              {activos.map((activo) => (
                <option key={activo.id} value={activo.id}>
                  {activo.nombre} · {activo.codigo}
                </option>
              ))}
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Etiqueta htmlFor="sub-activo-estado">Estado</Etiqueta>
            <Select
              id="sub-activo-estado"
              className={CONTROL}
              value={estado}
              onChange={(evento) => setEstado(evento.target.value === 'desuso' ? 'desuso' : 'activo')}
            >
              <option value="activo">Activo</option>
              <option value="desuso">En desuso</option>
            </Select>
          </div>

          <Input
            label="Código"
            value={codigo}
            maxLength={MAX_CODIGO}
            placeholder="Ej. MANGUERA-01"
            className="font-mono"
            error={codigoLimpio.length > 0 ? (errorCodigo ?? undefined) : undefined}
            hint={codigoLimpio.length === 0 ? 'Único en todo el sistema' : undefined}
            onChange={(evento) => setCodigo(evento.target.value)}
          />

          <Input
            label="Nombre"
            value={nombre}
            maxLength={MAX_NOMBRE}
            placeholder="Ej. Manguera principal"
            error={nombreLimpio.length > 0 ? (errorNombre ?? undefined) : undefined}
            hint={nombreLimpio.length === 0 ? 'Hasta 150 caracteres' : undefined}
            onChange={(evento) => setNombre(evento.target.value)}
          />

          <Input
            label="Tipo de componente"
            value={tipo}
            maxLength={MAX_TIPO}
            placeholder="Ej. Motor, Sensor, Manguera"
            onChange={(evento) => setTipo(evento.target.value)}
          />

          <Input
            label="Umbral de galonaje"
            type="number"
            min={0}
            step="0.01"
            value={umbral}
            hint="Galones que admite antes de pedir mantenimiento"
            onChange={(evento) => setUmbral(evento.target.value)}
          />

          <Input
            label="Lectura actual"
            type="number"
            min={0}
            step="0.01"
            value={lecturaActual}
            hint="Totalizador del surtidor"
            onChange={(evento) => setLecturaActual(evento.target.value)}
          />

          <Input
            label="Lectura del último cambio"
            type="number"
            min={0}
            step="0.01"
            value={lecturaCambio}
            hint="Se actualiza al registrar un mantenimiento"
            onChange={(evento) => setLecturaCambio(evento.target.value)}
          />

          <Input
            label="Último mantenimiento"
            type="date"
            value={ultimo}
            onChange={(evento) => setUltimo(evento.target.value)}
          />

          <Input
            label="Próximo mantenimiento"
            type="date"
            value={proximo}
            onChange={(evento) => setProximo(evento.target.value)}
          />
        </div>

        <div
          className={cn(
            'flex flex-wrap items-center justify-between gap-3 rounded-card border p-3',
            progreso >= 100
              ? 'border-rose-200 bg-rose-50'
              : progreso >= 80
                ? 'border-amber-200 bg-amber-50'
                : 'border-ink-100 bg-ink-50/60',
          )}
        >
          <span className="text-sm text-ink-700">
            Consumo: <strong>{formatNumber(usado)}</strong> de{' '}
            <strong>{formatNumber(limite)}</strong> galones
          </span>
          <span className="text-sm font-semibold text-ink-800">{progreso}% del umbral</span>
        </div>

        <Checkbox
          label="Marcar como crítico"
          hint="Se resalta en el listado y en el dashboard, aunque no haya alcanzado el umbral"
          checked={critico}
          onChange={(evento) => setCritico(evento.target.checked)}
        />

        <div className="flex flex-col gap-1.5">
          <Etiqueta htmlFor="sub-activo-descripcion">Descripción</Etiqueta>
          <textarea
            id="sub-activo-descripcion"
            rows={3}
            value={descripcion}
            placeholder="Detalles del componente"
            className="w-full rounded-control border border-ink-200 bg-white px-4 py-3 text-sm text-ink-900 placeholder:text-ink-400 outline-none transition-all duration-200 focus:border-brand-400 focus:ring-4 focus:ring-brand-100"
            onChange={(evento) => setDescripcion(evento.target.value)}
          />
        </div>
      </div>
    </Dialog>
  );
}
